import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { User2 } from "lucide-react";
import { Field, Input, Select, ChipGroup, Textarea, SectionTitle } from "../primitives";
import { LANGUAGES, AVAILABILITY_STATUS } from "../constants";

export default function Step1Basic({ form, set, role }) {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/meta/countries");
        setCountries(data.countries || []);
      } catch (e) { console.warn("[Step1Basic] countries:", e); }
    })();
  }, []);

  useEffect(() => {
    if (!form.country_code) return;
    (async () => {
      try {
        const { data } = await api.get("/meta/cities", { params: { country_code: form.country_code } });
        setCities(data.cities || []);
        if (!form.city && data.cities?.[0]) set({ city: data.cities[0].name });
      } catch (e) { console.warn("[Step1Basic] cities:", e); }
    })();
  }, [form.country_code]);

  return (
    <div className="space-y-6">
      <SectionTitle icon={User2} title="Basic information"
        subtitle="The essentials clients see first — keep it accurate and inviting." />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Display name" required testid="display-name">
          <Input
            value={form.display_name}
            onChange={(e) => set({ display_name: e.target.value })}
            placeholder="e.g. Anastasia"
            maxLength={40}
            data-testid="basic-display-name"
          />
        </Field>
        <Field label="Age" required testid="age">
          <Input
            type="number" min={18} max={75}
            value={form.age}
            onChange={(e) => set({ age: e.target.value })}
            data-testid="basic-age"
          />
        </Field>
        <Field label="Nationality" testid="nationality">
          <Input
            value={form.nationality}
            onChange={(e) => set({ nationality: e.target.value })}
            placeholder="e.g. Montenegrin"
            data-testid="basic-nationality"
          />
        </Field>
        <Field label="Profile type">
          <Select
            value={form.is_independent ? "independent" : "agency"}
            disabled={role === "agency"}
            onChange={(e) => set({ is_independent: e.target.value === "independent" })}
            data-testid="basic-type"
          >
            <option value="independent">Independent</option>
            <option value="agency">Agency</option>
          </Select>
        </Field>
        <Field label="Country" required>
          <Select
            value={form.country_code}
            onChange={(e) => set({ country_code: e.target.value, city: "" })}
            data-testid="basic-country"
          >
            <option value="">Select country</option>
            {countries.map(c => <option key={c.code} value={c.code}>{c.name_en}</option>)}
          </Select>
        </Field>
        <Field label="City" required>
          <Select
            value={form.city}
            onChange={(e) => set({ city: e.target.value })}
            data-testid="basic-city"
          >
            <option value="">Select city</option>
            {cities.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </Select>
        </Field>
        <Field label="Area / district" testid="area">
          <Input
            value={form.area}
            onChange={(e) => set({ area: e.target.value })}
            placeholder="e.g. Budva Marina"
            data-testid="basic-area"
          />
        </Field>
        <Field label="Availability status">
          <Select
            value={form.availability_status}
            onChange={(e) => set({ availability_status: e.target.value })}
            data-testid="basic-availability"
          >
            {AVAILABILITY_STATUS.map(a => <option key={a.v} value={a.v}>{a.label}</option>)}
          </Select>
        </Field>
      </div>

      <Field label="Languages spoken" required hint="Choose every language you can confidently converse in.">
        <ChipGroup
          options={LANGUAGES}
          value={form.languages}
          onChange={(languages) => set({ languages })}
          testidPrefix="basic-lang"
        />
      </Field>

      <Field label="Short headline" hint="One line shown on listing cards. Max 80 characters.">
        <Textarea
          value={form.headline}
          onChange={(e) => set({ headline: e.target.value.slice(0, 80) })}
          placeholder="A sophisticated companion for unforgettable evenings."
          className="min-h-[64px]"
          data-testid="basic-headline"
        />
        <div className="text-[10px] text-stone-500 text-right mt-1">{(form.headline || "").length}/80</div>
      </Field>
    </div>
  );
}
